import { useGenerateReportPdfMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { generateZipFileFromUrls } from '@/utils';

export const useBulkReportDownload = ({
  selectedReportIds,
  setLoading,
}: {
  selectedReportIds: string[];
  setLoading: (loading: boolean) => void;
}) => {
  const [generateReportPdf] = useGenerateReportPdfMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled', isReportDownload: true });

  const handleBulkDownload = async () => {
    try {
      setLoading(true);

      // Show initial message before network call
      showSnackBar(
        'Preparing your reports. Download will start once ready, you can continue working.',
        'info',
        { persistent: true }
      );

      const { data } = await generateReportPdf({
        variables: { reportIds: selectedReportIds },
      });

      // Show success message after network response
      showSnackBar('Your reports are ready. Download is starting...', 'success');

      if (
        data &&
        data.generateReportPdf.downloadUrl &&
        data.generateReportPdf.downloadUrl.length > 0
      ) {
        // Add a delay to ensure the second snackbar is visible before starting download
        setTimeout(async () => {
          // If there's only one PDF, download it directly without zipping
          if (
            data.generateReportPdf.downloadUrl &&
            data.generateReportPdf.downloadUrl.length === 1
          ) {
            const downloadUrl = data.generateReportPdf.downloadUrl.at(0);
            if (downloadUrl) {
              // Open the download URL directly
              window.open(downloadUrl, '_blank');
            }
          } else if (data.generateReportPdf.downloadUrl) {
            // Multiple PDFs - create a zip file
            await generateZipFileFromUrls(
              data.generateReportPdf.downloadUrl,
              data.generateReportPdf.filename
            );
          }

          // Show final success message after download
          setTimeout(() => {
            showSnackBar('Your reports have been downloaded successfully', 'success');
          }, 500);
        }, 2000);
      }
    } catch (error) {
      // Show error message
      showSnackBar('Download failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  return {
    handleBulkDownload,
  };
};
